const fetchCartRequest = async (url, options = {}, timeout = 6500) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
const nativeCartMode = document.documentElement.hasAttribute('data-native-cart');

document.addEventListener('click', (event) => {
  if (!nativeCartMode) return;
  const recommendationNavButton = event.target.closest('[data-cart-recommendation-prev], [data-cart-recommendation-next]');
  if (recommendationNavButton) {
    const section = recommendationNavButton.closest('.cart-page__recommendations');
    const grid = section?.querySelector('[data-cart-page-recommendations]');
    const cards = grid ? [...grid.querySelectorAll('[data-cart-page-recommendation]')] : [];
    const perPage = window.matchMedia('(max-width: 700px)').matches ? 1 : 2;
    const pageCount = Math.ceil(cards.length / perPage);
    if (!grid || pageCount <= 1) return;
    const direction = recommendationNavButton.hasAttribute('data-cart-recommendation-next') ? 1 : -1;
    const currentPage = Number(grid.dataset.page) || 0;
    const nextPage = (currentPage + direction + pageCount) % pageCount;
    const firstVisibleIndex = nextPage * perPage;
    grid.dataset.page = String(nextPage);
    cards.forEach((card, index) => {
      card.hidden = index < firstVisibleIndex || index >= firstVisibleIndex + perPage;
    });
    return;
  }

  const recommendationButton = event.target.closest('[data-native-recommendation-add]');
  if (recommendationButton) {
    const variantId = recommendationButton.dataset.variantId;
    if (!variantId || recommendationButton.disabled) return;
    recommendationButton.disabled = true;
    const form = document.createElement('form');
    form.method = 'post';
    form.action = `${window.Shopify.routes.root}cart/add`;
    const id = document.createElement('input');
    id.type = 'hidden';
    id.name = 'id';
    id.value = variantId;
    const returnTo = document.createElement('input');
    returnTo.type = 'hidden';
    returnTo.name = 'return_to';
    returnTo.value = window.location.pathname;
    form.append(id, returnTo);
    document.body.append(form);
    form.requestSubmit();
    return;
  }

  const button = event.target.closest('[data-native-cart-minus], [data-native-cart-plus]');
  if (!button) return;
  const form = button.closest('[data-native-cart-line]');
  const input = form?.querySelector('input[name="quantity"]');
  if (!form || !input || form.dataset.submitting === 'true') return;
  const change = button.hasAttribute('data-native-cart-plus') ? 1 : -1;
  input.value = String(Math.max(1, Math.floor(Number(input.value) || 1) + change));
  form.dataset.submitting = 'true';
  form.requestSubmit();
});

document.querySelectorAll('[data-cart-page-recommendations]').forEach((grid) => {
  const cards = [...grid.querySelectorAll('[data-cart-page-recommendation]')];
  const perPage = window.matchMedia('(max-width: 700px)').matches ? 1 : 2;
  const section = grid.closest('.cart-page__recommendations');
  section?.querySelectorAll('[data-cart-recommendation-prev], [data-cart-recommendation-next]').forEach((button) => {
    button.disabled = cards.length <= perPage;
  });
});

document.addEventListener('change', (event) => {
  if (!nativeCartMode) return;
  const input = event.target.closest('[data-native-cart-line] input[name="quantity"]');
  if (input) {
    const form = input.closest('[data-native-cart-line]');
    input.value = String(Math.max(1, Math.floor(Number(input.value) || 1)));
    if (!form || form.dataset.submitting === 'true') return;
    form.dataset.submitting = 'true';
    form.requestSubmit();
    return;
  }
  const cartPageInput = event.target.closest('.cart-page__form input[name="updates[]"]');
  if (!cartPageInput) return;
  const cartPageForm = cartPageInput.closest('.cart-page__form');
  cartPageInput.value = String(Math.max(0, Math.floor(Number(cartPageInput.value) || 0)));
  if (!cartPageForm || cartPageForm.dataset.submitting === 'true') return;
  cartPageForm.dataset.submitting = 'true';
  cartPageForm.requestSubmit();
});

const CART_SECTION_ID = 'cart-rail';
const CART_RATE_LIMIT_RETRIES = 2;
const CART_MAX_RETRY_DELAY = 2500;
const CART_QUANTITY_BATCH_DELAY = 120;

const cartSectionsPayload = () => ({
  sections: [CART_SECTION_ID],
  sections_url: `${window.location.pathname}${window.location.search}`
});

const retryAfterMilliseconds = (response, attempt) => {
  const retryAfter = response.headers.get('Retry-After');
  if (retryAfter) {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds)) return Math.min(CART_MAX_RETRY_DELAY, Math.max(800, seconds * 1000));
    const date = Date.parse(retryAfter);
    if (!Number.isNaN(date)) return Math.min(CART_MAX_RETRY_DELAY, Math.max(800, date - Date.now()));
  }
  return Math.min(CART_MAX_RETRY_DELAY, 800 * (2 ** attempt));
};

const fetchCartMutation = async (url, options = {}) => {
  for (let attempt = 0; ; attempt += 1) {
    const response = await fetch(url, options);
    if (response.status !== 429 || attempt >= CART_RATE_LIMIT_RETRIES) return response;
    document.querySelector('cart-drawer')?.setPending('요청이 많아 잠시 후 다시 시도합니다…');
    await wait(retryAfterMilliseconds(response, attempt));
  }
};

const parseCartResponse = async (response, fallbackMessage) => {
  const responseText = await response.text();
  let payload;
  try { payload = JSON.parse(responseText); } catch (_) {}
  if (!response.ok) {
    const message = payload?.description || payload?.message || `${fallbackMessage}${response.status ? ` (${response.status})` : ''}`;
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  if (!payload) throw new Error(fallbackMessage);
  return payload;
};

class CartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.desktopMedia) return;
    this.desktopMedia = window.matchMedia('(min-width: 1280px)');
    this.handleBreakpointChange = () => this.syncState();
    this.desktopMedia.addEventListener('change', this.handleBreakpointChange);
    this.syncState();
  }

  disconnectedCallback() {
    this.desktopMedia?.removeEventListener('change', this.handleBreakpointChange);
  }

  open() { this.classList.add('is-open'); }
  close() { this.classList.remove('is-open'); }

  collapse() {
    this.classList.add('is-collapsed');
    this.syncState();
  }

  expand() {
    this.classList.remove('is-collapsed');
    this.syncState();
  }

  toggle() {
    if (this.desktopMedia?.matches) {
      if (this.classList.contains('is-collapsed')) this.expand();
      else this.collapse();
      return;
    }
    if (this.classList.contains('is-open')) this.close();
    else this.open();
  }

  syncState() {
    const collapsed = Boolean(this.desktopMedia?.matches && this.classList.contains('is-collapsed'));
    document.documentElement.classList.toggle('cart-rail-collapsed', collapsed);
    const toggle = this.querySelector('[data-cart-toggle]');
    if (!toggle) return;
    toggle.setAttribute('aria-expanded', String(!collapsed));
    toggle.setAttribute('aria-label', collapsed ? toggle.dataset.expandLabel : toggle.dataset.collapseLabel);
  }

  setPending(message = '') {
    const status = this.querySelector('[data-cart-pending]');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
    this.toggleAttribute('aria-busy', Boolean(message));
  }

  applySectionHtml(html, openAfter = true) {
    if (!html) return;
    const previousScroll = this.querySelector('.cart-rail')?.scrollTop || 0;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.querySelector('cart-drawer');
    if (!replacement) throw new Error('Unable to render cart.');
    this.innerHTML = replacement.innerHTML;
    this.dataset.cartItemCount = replacement.dataset.cartItemCount || '0';
    this.dataset.cartTotalCents = replacement.dataset.cartTotalCents || '0';
    this.syncState();
    const rail = this.querySelector('.cart-rail');
    if (rail) rail.scrollTop = previousScroll;
    const itemCount = Math.max(0, Number(this.dataset.cartItemCount) || 0);
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = itemCount;
      badge.classList.toggle('is-empty', itemCount === 0);
    });
    reapplyPendingQuantityDisplay(this);
    if (cartQueuedOperationCount > 0 && cartBusyMessage) this.setPending(cartBusyMessage);
    if (openAfter) this.open();
  }

  async refresh(openAfter = true) {
    const response = await fetch(`${window.Shopify.routes.root}cart?section_id=${CART_SECTION_ID}`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Unable to refresh cart.');
    this.applySectionHtml(await response.text(), openAfter);
  }
}

class QuickView extends HTMLElement {
  open() { this.classList.add('is-open'); document.documentElement.classList.add('drawer-open'); }
  close() { this.classList.remove('is-open'); document.documentElement.classList.remove('drawer-open'); }

  loadHtml(html) {
    const content = this.querySelector('[data-quick-view-content]');
    content.innerHTML = html;
    this.open();
    if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
  }

  async load(url) {
    const content = this.querySelector('[data-quick-view-content]');
    content.innerHTML = '<div class="quick-view-loading">Loading…</div>';
    this.open();
    try {
      const response = await fetchCartRequest(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Unable to load product options.');
      const html = await response.text();
      if (!html.trim()) throw new Error('Product options are unavailable.');
      this.loadHtml(html);
    } catch (error) {
      content.innerHTML = `<div class="quick-view-loading">${error.message}</div>`;
    }
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
if (!customElements.get('quick-view')) customElements.define('quick-view', QuickView);

let cartOperationQueue = Promise.resolve();
let cartQueuedOperationCount = 0;
let quantityIntents = new Map();
let quantityBatchQueued = false;
let cartBusyMessage = '';
let cartErrorTimer;

const showCartError = (message) => {
  const drawer = document.querySelector('cart-drawer');
  window.clearTimeout(cartErrorTimer);
  drawer?.setPending(message || '장바구니를 업데이트하지 못했습니다. 다시 시도해 주세요.');
  cartErrorTimer = window.setTimeout(() => {
    cartErrorTimer = undefined;
    if (cartQueuedOperationCount === 0) drawer?.setPending('');
  }, 4500);
};

const announceCartBusy = (message) => {
  window.clearTimeout(cartErrorTimer);
  cartErrorTimer = undefined;
  cartBusyMessage = message;
  document.querySelector('cart-drawer')?.setPending(message);
};

const enqueueCartOperation = (message, operation) => {
  cartQueuedOperationCount += 1;
  announceCartBusy(message);
  const run = async () => {
    announceCartBusy(message);
    return operation();
  };
  const queued = cartOperationQueue.then(run, run).finally(() => {
    cartQueuedOperationCount = Math.max(0, cartQueuedOperationCount - 1);
    if (cartQueuedOperationCount === 0) {
      cartBusyMessage = '';
      if (!cartErrorTimer) document.querySelector('cart-drawer')?.setPending('');
      document.dispatchEvent(new CustomEvent('cart:idle'));
    }
  });
  cartOperationQueue = queued.catch(() => {});
  return queued;
};

const applyCartPayload = async (payload, openAfter = true) => {
  const drawer = document.querySelector('cart-drawer');
  const sectionHtml = payload?.sections?.[CART_SECTION_ID];
  if (sectionHtml) drawer?.applySectionHtml(sectionHtml, openAfter);
  else await drawer?.refresh(openAfter);
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: Number(drawer?.dataset.cartItemCount || 0) } }));
};

const recoverAuthoritativeCart = async (openAfter = true) => {
  const drawer = document.querySelector('cart-drawer');
  try { await drawer?.refresh(openAfter); } catch (_) {}
};

const readCartQuantity = (control) => {
  const quantityElement = control?.querySelector('[data-cart-quantity-value]');
  const value = quantityElement instanceof HTMLInputElement ? quantityElement.value : quantityElement?.textContent;
  return Math.max(0, Math.floor(Number(value) || 0));
};

const writeCartQuantity = (control, quantity) => {
  const quantityElement = control?.querySelector('[data-cart-quantity-value]');
  if (!quantityElement) return;
  if (quantityElement instanceof HTMLInputElement) quantityElement.value = String(quantity);
  else quantityElement.textContent = String(quantity);
};

const mergeQuantityIntent = (key, intent) => {
  const existing = quantityIntents.get(key);
  if (intent.type === 'remove') {
    quantityIntents.set(key, intent);
    return;
  }
  if (intent.type === 'absolute') {
    quantityIntents.set(key, intent);
    return;
  }
  if (existing?.type === 'remove') return;
  if (existing?.type === 'absolute') {
    quantityIntents.set(key, { type: 'absolute', quantity: Math.max(1, existing.quantity + intent.delta) });
    return;
  }
  quantityIntents.set(key, { type: 'delta', delta: (existing?.delta || 0) + intent.delta });
};

const reapplyPendingQuantityDisplay = (drawer = document.querySelector('cart-drawer')) => {
  if (!drawer || quantityIntents.size === 0) return;
  quantityIntents.forEach((intent, key) => {
    const control = [...drawer.querySelectorAll('[data-cart-quantity]')].find((element) => element.dataset.key === key);
    if (!control) return;
    const row = control.closest('.cart-row');
    if (intent.type === 'remove') {
      if (row) row.hidden = true;
      return;
    }
    const base = readCartQuantity(control);
    const quantity = intent.type === 'absolute' ? intent.quantity : Math.max(1, base + intent.delta);
    writeCartQuantity(control, quantity);
  });
};

const fetchAuthoritativeCart = async () => {
  const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
  });
  return parseCartResponse(response, 'Unable to read cart.');
};

const updateCartQuantities = async (intents) => {
  const cart = await fetchAuthoritativeCart();
  const itemsByKey = new Map(cart.items.map((item) => [item.key, item]));
  const updates = {};
  intents.forEach((intent, key) => {
    const current = itemsByKey.get(key);
    if (!current) return;
    if (intent.type === 'remove') updates[key] = 0;
    else if (intent.type === 'absolute') updates[key] = Math.max(1, intent.quantity);
    else updates[key] = Math.max(1, current.quantity + intent.delta);
  });
  if (Object.keys(updates).length === 0) {
    await recoverAuthoritativeCart(true);
    return;
  }
  const response = await fetchCartMutation(`${window.Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ updates, ...cartSectionsPayload() })
  });
  const payload = await parseCartResponse(response, 'Unable to update cart.');
  await applyCartPayload(payload, true);
};

const queueQuantityBatch = () => {
  if (quantityBatchQueued) return;
  quantityBatchQueued = true;
  enqueueCartOperation('장바구니 업데이트 중…', async () => {
    await wait(CART_QUANTITY_BATCH_DELAY);
    const batch = quantityIntents;
    quantityIntents = new Map();
    quantityBatchQueued = false;
    try {
      await updateCartQuantities(batch);
    } catch (error) {
      await recoverAuthoritativeCart(true);
      showCartError(error.message);
    }
  });
};

const recordQuantityIntent = (key, intent, control, row) => {
  if (!key) return;
  mergeQuantityIntent(key, intent);
  if (intent.type === 'remove') {
    if (row) {
      row.hidden = true;
      row.setAttribute('aria-busy', 'true');
    }
  } else if (control) {
    const current = readCartQuantity(control);
    const quantity = intent.type === 'absolute' ? intent.quantity : Math.max(1, current + intent.delta);
    writeCartQuantity(control, quantity);
  }
  queueQuantityBatch();
};

const productFormItem = (form) => {
  const formData = new FormData(form);
  const id = formData.get('id');
  if (!id) return null;
  const item = { id: String(id), quantity: Math.max(1, Math.floor(Number(formData.get('quantity')) || 1)) };
  const sellingPlan = formData.get('selling_plan');
  if (sellingPlan) item.selling_plan = String(sellingPlan);
  const properties = {};
  for (const [name, value] of formData.entries()) {
    const match = name.match(/^properties\[(.+)\]$/);
    if (match && String(value).trim()) properties[match[1]] = String(value);
  }
  if (Object.keys(properties).length) item.properties = properties;
  return item;
};

document.addEventListener('submit', (event) => {
  const form = event.target.closest('form[action*="/cart/add"]');
  if (!form || form.querySelector('input[name="return_to"]')) return;
  if (event.submitter?.name === 'checkout') return;
  event.preventDefault();
  if (form.dataset.cartSubmitting === 'true') return;
  const item = productFormItem(form);
  if (!item) return;

  form.dataset.cartSubmitting = 'true';
  const submitter = event.submitter || form.querySelector('[type="submit"], button:not([type])');
  if (submitter) submitter.disabled = true;

  enqueueCartOperation('상품을 장바구니에 담는 중…', async () => {
    try {
      const response = await fetchCartMutation(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [item], ...cartSectionsPayload() })
      });
      const payload = await parseCartResponse(response, 'Unable to add product.');
      await applyCartPayload(payload, true);
      document.querySelector('quick-view')?.close();
    } catch (error) {
      await recoverAuthoritativeCart(true);
      showCartError(error.message);
    } finally {
      delete form.dataset.cartSubmitting;
      if (submitter) submitter.disabled = false;
    }
  });
}, true);

document.addEventListener('click', async (event) => {
  const drawer = document.querySelector('cart-drawer');
  const quickView = document.querySelector('quick-view');
  if (event.target.closest('[data-cart-open]')) {
    event.preventDefault();
    drawer?.open();
  }
  if (event.target.closest('[data-cart-close]')) drawer?.close();
  if (event.target.closest('[data-cart-toggle]')) {
    event.preventDefault();
    drawer?.toggle();
  }
  if (event.target.closest('[data-quick-close]')) quickView?.close();

  const quickViewButton = event.target.closest('[data-quick-view], [data-quick-view-url]');
  if (quickViewButton) {
    event.preventDefault();
    const template = quickViewButton.closest('.product-card, .cart-recommendation')?.querySelector('[data-quick-view-template]');
    if (template) quickView?.loadHtml(template.innerHTML);
    else if (quickViewButton.dataset.quickViewUrl) await quickView?.load(quickViewButton.dataset.quickViewUrl);
  }

  const cartChange = event.target.closest('[data-cart-change]');
  if (cartChange) {
    event.preventDefault();
    const action = cartChange.dataset.action;
    const quantityControl = cartChange.closest('.cart-row')?.querySelector('[data-cart-quantity]');
    const key = cartChange.dataset.key || quantityControl?.dataset.key;
    const row = cartChange.closest('.cart-row');
    if (action === 'remove') recordQuantityIntent(key, { type: 'remove' }, quantityControl, row);
    else recordQuantityIntent(key, { type: 'delta', delta: action === 'increase' ? 1 : -1 }, quantityControl, row);
  }

  const recommendationDirection = event.target.closest('[data-recommendation-prev], [data-recommendation-next]');
  if (recommendationDirection) {
    const recommendationArea = recommendationDirection.closest('[data-recommendations]');
    const cards = [...recommendationArea.querySelectorAll('[data-recommendation-card]')];
    const activeIndex = Math.max(0, cards.findIndex((card) => card.classList.contains('is-active')));
    const change = recommendationDirection.hasAttribute('data-recommendation-next') ? 1 : -1;
    const nextIndex = (activeIndex + change + cards.length) % cards.length;
    cards.forEach((card, index) => card.classList.toggle('is-active', index === nextIndex));
  }

  const quantityButton = event.target.closest('[data-qty-minus], [data-qty-plus]');
  if (quantityButton) {
    const input = quantityButton.parentElement.querySelector('input[type="number"]');
    const change = quantityButton.hasAttribute('data-qty-plus') ? 1 : -1;
    input.value = Math.max(Number(input.min) || 1, Number(input.value) + change);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
});

document.addEventListener('cart:refresh', () => {
  enqueueCartOperation('장바구니 동기화 중…', async () => {
    try { await document.querySelector('cart-drawer')?.refresh(); }
    catch (error) { showCartError(error.message); }
  });
});

document.addEventListener('change', (event) => {
  const cartQuantityInput = event.target.closest('[data-cart-quantity-value]');
  if (cartQuantityInput) {
    const quantityControl = cartQuantityInput.closest('[data-cart-quantity]');
    const row = cartQuantityInput.closest('.cart-row');
    const key = quantityControl?.dataset.key;
    const quantity = Math.max(1, Math.floor(Number(cartQuantityInput.value) || 1));
    cartQuantityInput.value = String(quantity);
    recordQuantityIntent(key, { type: 'absolute', quantity }, quantityControl, row);
    return;
  }

  const select = event.target.closest('[data-variant-select]');
  if (!select) return;
  const selectedOption = select.options[select.selectedIndex];
  const quickProduct = select.closest('.quick-product');
  if (quickProduct) {
    const image = quickProduct.querySelector('[data-variant-image]');
    if (image && selectedOption.dataset.image) {
      image.src = selectedOption.dataset.image;
      image.removeAttribute('srcset');
    }
  }
  const productLayout = select.closest('.product-layout');
  if (productLayout) {
    const gallery = productLayout.querySelector('[data-product-gallery]');
    const mediaId = selectedOption.dataset.mediaId;
    const matchingThumb = mediaId ? gallery?.querySelector(`[data-gallery-thumb][data-media-id="${mediaId}"]`) : null;
    if (matchingThumb) matchingThumb.click();
    else {
      const main = gallery?.querySelector('[data-gallery-main]');
      if (main && selectedOption.dataset.image) {
        main.src = selectedOption.dataset.image;
        main.removeAttribute('srcset');
      }
    }
  }
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cart-page-minus], [data-cart-page-plus]');
  if (!button) return;
  event.preventDefault();
  const input = button.parentElement.querySelector('input[name="updates[]"]');
  if (!input) return;
  const form = input.closest('.cart-page__form');
  if (!form || form.dataset.submitting === 'true') return;
  const change = button.hasAttribute('data-cart-page-plus') ? 1 : -1;
  input.value = Math.max(0, Number(input.value || 0) + change);
  form.dataset.submitting = 'true';
  form.requestSubmit();
});
document.querySelectorAll('[data-timer]').forEach((element) => {
  const configuredEnd = Date.parse(element.dataset.timerEnd);
  const end = Number.isNaN(configuredEnd) ? Date.now() + 7 * 864e5 : configuredEnd;
  const updateTimer = () => {
    const rawRemaining = end - Date.now();
    const remaining = Math.max(0, rawRemaining);
    const parts = [Math.floor(remaining / 864e5), Math.floor(remaining / 36e5) % 24, Math.floor(remaining / 6e4) % 60, Math.floor(remaining / 1e3) % 60];
    element.querySelectorAll('[data-time-value]').forEach((node, index) => node.textContent = String(parts[index]).padStart(2, '0'));
    if (rawRemaining <= 0) {
      const block = element.closest('[data-countdown-block]');
      const endText = block?.querySelector('[data-countdown-end-text]');
      if (endText) endText.hidden = false;
      if (block?.dataset.hideWhenEnded === 'true') element.hidden = true;
    }
  };
  updateTimer();
  setInterval(updateTimer, 1000);
});

const initializeTestimonials = (slideshow) => {
  if (!slideshow || slideshow.dataset.carouselInitialized === 'true') return;
  const slides = [...slideshow.querySelectorAll('[data-testimonial-slide]')];
  if (!slides.length) return;
  slideshow.dataset.carouselInitialized = 'true';
  const currentLabel = slideshow.querySelector('[data-testimonial-current]');
  const pauseButton = slideshow.querySelector('[data-testimonial-pause]');
  let current = 0;
  let paused = false;
  let rotation;
  const show = (index) => {
    current = (index + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === current));
    if (currentLabel) currentLabel.textContent = String(current + 1);
  };
  const stop = () => clearInterval(rotation);
  const start = () => {
    stop();
    if (slideshow.dataset.autoplay === 'true' && !paused && slides.length > 1) {
      rotation = setInterval(() => show(current + 1), Number(slideshow.dataset.interval) || 7000);
    }
  };
  slideshow.querySelector('[data-testimonial-prev]')?.addEventListener('click', () => { show(current - 1); start(); });
  slideshow.querySelector('[data-testimonial-next]')?.addEventListener('click', () => { show(current + 1); start(); });
  pauseButton?.addEventListener('click', () => {
    paused = !paused;
    pauseButton.textContent = paused ? '▶' : 'Ⅱ';
    pauseButton.setAttribute('aria-label', paused ? 'Play testimonials' : 'Pause testimonials');
    start();
  });
  slideshow.addEventListener('mouseenter', stop);
  slideshow.addEventListener('mouseleave', start);
  show(0);
  start();
};

const initializeBlogPosts = (section) => {
  if (!section || section.dataset.carouselInitialized === 'true') return;
  const track = section.querySelector('.blog-posts__track');
  if (!track) return;
  section.dataset.carouselInitialized = 'true';
  const move = (direction) => track.scrollBy({ left: direction * Math.max(280, track.clientWidth * 0.8), behavior: 'smooth' });
  section.querySelector('[data-blog-prev]')?.addEventListener('click', () => move(-1));
  section.querySelector('[data-blog-next]')?.addEventListener('click', () => move(1));
};

const initializeThemeEditorSections = (root = document) => {
  root.querySelectorAll('[data-testimonials]').forEach(initializeTestimonials);
  root.querySelectorAll('[data-blog-posts]').forEach(initializeBlogPosts);
};

initializeThemeEditorSections();

document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
  const main = gallery.querySelector('[data-gallery-main]');
  const stage = gallery.querySelector('[data-gallery-stage]');
  const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
  const currentLabel = gallery.querySelector('[data-gallery-current]');
  if (!main || thumbs.length === 0) return;
  let current = Math.max(0, thumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
  let autoplayTimer;
  let manuallyControlled = false;
  let galleryInView = true;
  const autoplayEnabled = gallery.dataset.galleryAutoplay === 'true' && thumbs.length > 1;
  const autoplayInterval = Math.max(3000, Number(gallery.dataset.galleryInterval) || 5000);

  const showImage = (index, manual = false) => {
    current = (index + thumbs.length) % thumbs.length;
    const thumb = thumbs[current];
    main.src = thumb.dataset.image;
    main.removeAttribute('srcset');
    thumbs.forEach((button, buttonIndex) => {
      const active = buttonIndex === current;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    if (manual) {
      const thumbnailRail = thumb.parentElement;
      if (thumbnailRail?.scrollTo) {
        const left = thumb.offsetLeft - (thumbnailRail.clientWidth - thumb.offsetWidth) / 2;
        thumbnailRail.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }
    if (currentLabel) currentLabel.textContent = current + 1;
    if (manual) {
      manuallyControlled = true;
      clearInterval(autoplayTimer);
    }
  };
  const startAutoplay = () => {
    clearInterval(autoplayTimer);
    if (!autoplayEnabled || manuallyControlled || !galleryInView) return;
    autoplayTimer = setInterval(() => {
      if (!document.hidden) showImage(current + 1);
    }, autoplayInterval);
  };

  gallery.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) showImage(Number(thumb.dataset.galleryIndex), true);
    if (event.target.closest('[data-gallery-prev]')) showImage(current - 1, true);
    if (event.target.closest('[data-gallery-next]')) showImage(current + 1, true);
  });

  stage?.addEventListener('pointermove', (event) => {
    if (event.target.closest('[data-gallery-prev], [data-gallery-next], .product-gallery__count')) {
      stage.classList.remove('is-zooming');
      return;
    }
    if (event.pointerType === 'touch') return;
    clearInterval(autoplayTimer);
    const bounds = stage.getBoundingClientRect();
    main.style.transformOrigin = `${((event.clientX - bounds.left) / bounds.width) * 100}% ${((event.clientY - bounds.top) / bounds.height) * 100}%`;
    stage.classList.add('is-zooming');
  });
  stage?.addEventListener('pointerleave', () => {
    stage.classList.remove('is-zooming');
    startAutoplay();
  });
  stage?.addEventListener('pointerdown', () => {
    manuallyControlled = true;
    clearInterval(autoplayTimer);
  });

  if ('IntersectionObserver' in window) {
    const galleryObserver = new IntersectionObserver(([entry]) => {
      galleryInView = entry.isIntersecting;
      if (galleryInView) startAutoplay();
      else clearInterval(autoplayTimer);
    }, { threshold: 0.08 });
    galleryObserver.observe(gallery);
  } else {
    startAutoplay();
  }
});

const initializeHeroCarousel = (carousel) => {
  if (!carousel || carousel.dataset.carouselInitialized === 'true') return;
  const slides = [...carousel.querySelectorAll('[data-hero-slide]')];
  if (slides.length < 2) return;
  carousel.dataset.carouselInitialized = 'true';
  const currentLabel = carousel.querySelector('[data-hero-current]');
  let current = 0;
  let timer;
  const show = (index) => {
    current = (index + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === current));
    if (currentLabel) currentLabel.textContent = current + 1;
  };
  const start = () => {
    clearInterval(timer);
    timer = setInterval(() => show(current + 1), Number(carousel.dataset.interval) || 5000);
  };
  carousel.querySelector('[data-hero-prev]')?.addEventListener('click', () => { show(current - 1); start(); });
  carousel.querySelector('[data-hero-next]')?.addEventListener('click', () => { show(current + 1); start(); });
  carousel.addEventListener('mouseenter', () => clearInterval(timer));
  carousel.addEventListener('mouseleave', start);
  start();
};

document.querySelectorAll('[data-hero-carousel]').forEach(initializeHeroCarousel);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-hero-carousel]').forEach(initializeHeroCarousel);
  initializeThemeEditorSections(event.target);
});

document.querySelectorAll('[data-review-carousel]').forEach((carousel) => {
  const slides = [...carousel.querySelectorAll('[data-review-slide]')];
  if (slides.length < 2) return;
  const currentLabel = carousel.querySelector('[data-review-current]');
  let current = 0;
  let timer;
  const show = (index) => {
    current = (index + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === current));
    if (currentLabel) currentLabel.textContent = current + 1;
  };
  const start = () => {
    clearInterval(timer);
    timer = setInterval(() => {
      if (!document.hidden) show(current + 1);
    }, Math.max(3000, Number(carousel.dataset.interval) || 6000));
  };
  carousel.querySelector('[data-review-prev]')?.addEventListener('click', () => { show(current - 1); start(); });
  carousel.querySelector('[data-review-next]')?.addEventListener('click', () => { show(current + 1); start(); });
  carousel.addEventListener('mouseenter', () => clearInterval(timer));
  carousel.addEventListener('mouseleave', start);
  start();
});

if (document.body.classList.contains('template-product')) {
  const productLayout = document.querySelector('.product-layout');
  const assurance = document.querySelector('.product-assurance');
  let scrollFrame;
  const updateProductDetailMode = () => {
    scrollFrame = null;
    if (!productLayout || !assurance || window.innerWidth <= 900) {
      document.body.classList.remove('product-detail-view');
      return;
    }
    const start = productLayout.offsetTop + 180;
    const end = assurance.offsetTop - 180;
    document.body.classList.toggle('product-detail-view', window.scrollY > start && window.scrollY < end);
  };
  window.addEventListener('scroll', () => {
    if (!scrollFrame) scrollFrame = requestAnimationFrame(updateProductDetailMode);
  }, { passive: true });
  window.addEventListener('resize', updateProductDetailMode);
  updateProductDetailMode();
}

document.querySelectorAll('[data-category-carousel]').forEach((carousel) => {
  const track = carousel.querySelector('[data-category-track]');
  const progress = carousel.querySelector('[data-category-progress]');
  if (!track || !progress) return;
  const updateProgress = () => {
    const scrollable = Math.max(1, track.scrollWidth - track.clientWidth);
    const visibleRatio = Math.min(1, track.clientWidth / track.scrollWidth);
    const travel = 100 - visibleRatio * 100;
    progress.style.width = `${visibleRatio * 100}%`;
    progress.style.transform = `translateX(${(track.scrollLeft / scrollable) * travel / visibleRatio}%)`;
  };
  const move = (direction) => {
    const card = track.querySelector('.circle-card');
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: direction * ((card?.getBoundingClientRect().width || track.clientWidth) + gap), behavior: 'smooth' });
  };
  carousel.querySelector('[data-category-prev]')?.addEventListener('click', () => move(-1));
  carousel.querySelector('[data-category-next]')?.addEventListener('click', () => move(1));
  track.addEventListener('scroll', updateProgress, { passive: true });
  window.addEventListener('resize', updateProgress);
  updateProgress();
});

document.querySelectorAll('[data-related-carousel]').forEach((carousel) => {
  const track = carousel.querySelector('[data-related-track]');
  const progress = carousel.querySelector('[data-related-progress]');
  if (!track || !progress) return;
  const updateProgress = () => {
    const scrollable = Math.max(1, track.scrollWidth - track.clientWidth);
    const visibleRatio = Math.min(1, track.clientWidth / track.scrollWidth);
    const travel = 100 - visibleRatio * 100;
    progress.style.width = `${visibleRatio * 100}%`;
    progress.style.transform = `translateX(${(track.scrollLeft / scrollable) * travel / visibleRatio}%)`;
  };
  const move = (direction) => {
    const card = track.querySelector('.product-card');
    track.scrollBy({ left: direction * (card?.getBoundingClientRect().width || track.clientWidth), behavior: 'smooth' });
  };
  carousel.querySelector('[data-related-prev]')?.addEventListener('click', () => move(-1));
  carousel.querySelector('[data-related-next]')?.addEventListener('click', () => move(1));
  track.addEventListener('scroll', updateProgress, { passive: true });
  window.addEventListener('resize', updateProgress);
  updateProgress();
});

document.querySelectorAll('[data-price-filter]').forEach((filter) => {
  const rangeMin = filter.querySelector('[data-price-range-min]');
  const rangeMax = filter.querySelector('[data-price-range-max]');
  const numberMin = filter.querySelector('[data-price-number-min]');
  const numberMax = filter.querySelector('[data-price-number-max]');
  const slider = filter.querySelector('.price-filter__slider');
  const maximum = Number(filter.dataset.max) || 1;
  const update = (source) => {
    let minimumValue = Number(source === numberMin ? numberMin.value : rangeMin.value);
    let maximumValue = Number(source === numberMax ? numberMax.value : rangeMax.value);
    if (source === rangeMin || source === numberMin) minimumValue = Math.min(minimumValue, maximumValue);
    if (source === rangeMax || source === numberMax) maximumValue = Math.max(maximumValue, minimumValue);
    minimumValue = Math.max(0, Math.min(maximum, minimumValue));
    maximumValue = Math.max(0, Math.min(maximum, maximumValue));
    rangeMin.value = minimumValue;
    rangeMax.value = maximumValue;
    numberMin.value = minimumValue;
    numberMax.value = maximumValue;
    slider.style.setProperty('--price-min', `${minimumValue / maximum * 100}%`);
    slider.style.setProperty('--price-max', `${maximumValue / maximum * 100}%`);
  };
  [rangeMin, rangeMax, numberMin, numberMax].forEach((input) => input?.addEventListener('input', () => update(input)));
  update(rangeMin);
});

/* CYFIE KR existing product review placement 2026-10-27 */
(() => {
  const start = () => {
    if (!document.body.classList.contains('template-product')) return;
    const main = document.getElementById('MainContent');
    if (!main || main.dataset.cyfieReviewPlacement) return;
    main.dataset.cyfieReviewPlacement = 'bottom';
    const selector = '.jdgm-review-widget, #judgeme_product_reviews';
    let queued = false;
    let selectedWidget = null;

    const isDisplayed = (widget) => {
      const rect = widget.getBoundingClientRect();
      if (!rect.width || !rect.height || widget.closest('[hidden], [inert], template')) return false;
      for (let node = widget; node && node !== main.parentElement; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (style.display === 'none' || style.visibility !== 'visible' || Number(style.opacity) === 0) return false;
      }
      return true;
    };

    const placeExisting = () => {
      queued = false;
      if (!selectedWidget || !main.contains(selectedWidget)) {
        selectedWidget = Array.from(main.querySelectorAll(selector)).find((widget) => !widget.parentElement.closest(selector) && isDisplayed(widget));
      }
      const widgets = selectedWidget ? [selectedWidget] : [];
      for (const widget of widgets) {
        if (!isDisplayed(widget)) continue;
        const section = widget.closest('.shopify-section[id$="__judgeme_reviews"]');
        if (section && section.parentElement === main) {
          if (main.lastElementChild !== section) main.append(section);
          continue;
        }
        let host = main.querySelector(':scope > [data-cyfie-review-bottom]');
        if (!host) {
          host = document.createElement('div');
          host.className = 'section';
          host.dataset.cyfieReviewBottom = '';
          const page = document.createElement('div');
          page.className = 'page';
          host.append(page);
          main.append(host);
        } else if (main.lastElementChild !== host) {
          main.append(host);
        }
        const page = host.firstElementChild;
        if (widget.parentElement !== page) page.append(widget);
      }
    };

    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(placeExisting);
    };
    new MutationObserver((records) => {
      const relevant = records.some((record) => {
        if (record.target === main) return true;
        if (record.type === 'attributes') return record.target.matches(selector) || !!record.target.querySelector(selector);
        if (record.target.closest(selector)) return true;
        return Array.from(record.addedNodes).some((node) => node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector)));
      });
      if (relevant) schedule();
    }).observe(main, {childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'inert']});
    main.addEventListener('shopify:section:load', schedule);
    placeExisting();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();

